import { useAuth } from 'modules/auth/useAuth';
import { LoggedLayout } from 'modules/layouts/LoggedLayout/LoggedLayout';
import { ModalContainer } from 'modules/modal/ModalContainer';
import { lazy, Suspense, useLayoutEffect, useMemo } from 'react';
import { createBrowserRouter, Navigate, RouteObject, RouterProvider } from 'react-router-dom';
import { appConfig } from 'shared/appConfig';
import { ErrorBoundaryContainer } from 'shared/components/ErrorBoundaryContainer';
import { HistoryContainer } from 'shared/components/HistoryContainer';
import { PageLoadingSpinner } from 'shared/components/LoadingSpinner/PageLoadingSpinner';
import { useAuthStore } from 'shared/hooks/stores/useAuthStore';
import { useOnboardingStore } from 'shared/hooks/stores/useOnboardingStore';
import { DataFetchContainer } from './containers/DataFetchContainer';
import { AutoOnboardingPath } from './subcomponents/AutoOnboardingPath';
import { ExternalNavigate } from './subcomponents/ExternalNavigate';
import { PreserveJobOrderIdNavigate } from './subcomponents/PreserveJobOrderIdNavigate';
import { RedirectFromQueryToJobOrder } from './subcomponents/RedirectFromQueryToJobOrder';

const AuthPage = lazy(() => import('./AuthPage'));
const JobOrdersPage = lazy(() => import('./JobOrdersPage'));
const OnboardingPage = lazy(() => import('./OnboardingPage'));
const ApplicationsPage = lazy(() => import('./ApplicationsPage'));
const SettingsPage = lazy(() => import('./SettingsPage'));
const ProfilePage = lazy(() => import('./ProfilePage'));
const ObservedJobOrdersPage = lazy(() => import('./ObservedJobOrdersPage'));
const ExternalOfferPage = lazy(() => import('./ExternalOfferPage'));
const JobOrderPage = lazy(() => import('./JobOrderPage'));
const ExternalOfferApplySuccessPage = lazy(() => import('./ExternalOfferApplySuccessPage'));
const NotificationsPage = lazy(() => import('./NotificationsPage'));
const AirApplicationPage = lazy(() => import('./AirApplicationPage'));
const NotFoundPage = lazy(() => import('./NotFoundPage'));

const authRoutes = (onboardingFinished: boolean): RouteObject[] => [
  {
    path: '/',
    element: (
      <ErrorBoundaryContainer>
        <ModalContainer />
        <HistoryContainer />
        <DataFetchContainer />
        <RedirectFromQueryToJobOrder />
      </ErrorBoundaryContainer>
    ),
    children: [
      {
        path: '/offer/:offerId/success',
        element: <ExternalOfferApplySuccessPage />,
      },
      {
        path: '/offer/:offerId',
        element: <ExternalOfferPage />,
      },
      ...(appConfig.isAIREnabled
        ? [
            {
              path: '/air-application',
              children: [
                { index: true, element: <AirApplicationPage /> },
                { path: ':preppingId', element: <AirApplicationPage /> },
              ],
            },
          ]
        : []),
      ...(appConfig.jobBoardUrl
        ? [
            {
              path: '/job-board',
              children: [
                { index: true, element: <ExternalNavigate to={appConfig.jobBoardUrl} /> },
                {
                  path: ':offerId',
                  element: (
                    <ExternalNavigate to={new URL(':offerId', appConfig.jobBoardUrl).toString()} />
                  ),
                },
              ],
            },
          ]
        : []),
      ...(onboardingFinished
        ? [
            ...(appConfig.allowUserWithFinishedOnboardingBackToOnboarding
              ? [
                  {
                    path: '/onboarding',
                    children: [
                      { index: true, element: <OnboardingPage /> },
                      { path: ':stepName', element: <OnboardingPage /> },
                    ],
                  },
                ]
              : [
                  {
                    path: '/onboarding/*',
                    element: <Navigate to="/" replace />,
                  },
                ]),
            {
              path: '/',
              element: <LoggedLayout />,
              children: [
                { index: true, element: <JobOrdersPage /> },
                { path: '/applications', element: <ApplicationsPage /> },
                { path: '/job-orders/:jobOrderId/:offerId?', element: <JobOrderPage /> },
                {
                  path: '/profile',
                  children: [
                    {
                      index: true,
                      element: <ProfilePage />,
                    },
                    { path: ':tabName', element: <ProfilePage /> },
                  ],
                },
                { path: '/settings', element: <SettingsPage /> },
                { path: '/observed', element: <ObservedJobOrdersPage /> },
                { path: '/notifications', element: <NotificationsPage /> },
              ],
            },
            {
              path: '*',
              element: <NotFoundPage />,
            },
          ]
        : [
            {
              path: '/onboarding',
              children: [
                { index: true, element: <OnboardingPage /> },
                { path: ':stepName', element: <OnboardingPage /> },
              ],
            },
            {
              path: '/',
              element: <AutoOnboardingPath element={<LoggedLayout />} />,
              children: [
                { index: true, element: <JobOrdersPage /> },
                { path: '/job-orders/:jobOrderId', element: <JobOrderPage /> },
                { path: '/observed', element: <ObservedJobOrdersPage /> },
                { path: '/settings', element: <SettingsPage /> },
                { path: '/notifications', element: <NotificationsPage /> },
              ],
            },
            {
              path: '*',
              element: <Navigate to="/onboarding" />,
            },
          ]),
    ],
  },
];

const defaultRoutes: RouteObject[] = [
  {
    path: '/',
    element: <ErrorBoundaryContainer />,
    children: [
      {
        path: '/',
        element: <AuthPage />,
      },
      {
        path: '/offer/:offerId/success',
        element: <ExternalOfferApplySuccessPage />,
      },
      {
        path: '/offer/:offerId',
        element: <ExternalOfferPage />,
      },
      ...(appConfig.jobBoardUrl
        ? [
            {
              path: '/job-board',
              children: [
                { index: true, element: <ExternalNavigate to={appConfig.jobBoardUrl} /> },
                {
                  path: ':offerId',
                  element: (
                    <ExternalNavigate to={new URL(':offerId', appConfig.jobBoardUrl).toString()} />
                  ),
                },
              ],
            },
          ]
        : []),
      {
        path: '/job-orders/:jobOrderId',
        element: <PreserveJobOrderIdNavigate to="/" />,
      },
      {
        path: '*',
        element: <PreserveJobOrderIdNavigate to="/" />,
      },
    ],
  },
];

export const AppRouter = () => {
  const { verifyAuthentication, loadings } = useAuth(true);
  const authenticated = useAuthStore((state) => state.authenticated);
  const onboardingFinishedAt = useOnboardingStore((state) => state.finishedAt);

  const router = useMemo(
    () => createBrowserRouter(authenticated ? authRoutes(!!onboardingFinishedAt) : defaultRoutes),
    [authenticated, loadings.verifyAuth, onboardingFinishedAt],
  );

  useLayoutEffect(() => {
    verifyAuthentication();
  }, []);

  if (loadings.verifyAuth) {
    return <PageLoadingSpinner />;
  }

  return (
    <Suspense fallback={<PageLoadingSpinner />}>
      <RouterProvider router={router} />
    </Suspense>
  );
};
